import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import { api } from "../../services/api";
import { ArrowBack, Save } from "@mui/icons-material";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const FormCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const Input = styled.input`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.surface};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  min-height: 400px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary};
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const HelperText = styled.p`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  margin: 0;
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const VariablesInfo = styled.div`
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.hover};
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
`;

const VariablesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const VariableTag = styled.span`
  padding: 4px 8px;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
  border: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.primary}40;
  border-radius: ${(props) =>
    getTheme(props.theme?.isDark).borderRadius.sm};
  font-family: monospace;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      getTheme(props.theme?.isDark).colors.primary}30;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const VariableCategory = styled.div`
  margin-top: ${(props) =>
    getTheme(props.theme?.isDark).spacing.sm};

  &:first-child {
    margin-top: 0;
  }
`;

const VariableCategoryTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const PROMPT_KEYS = {
  ASSISTANT_INSTRUCTIONS: "assistant_instructions",
  MEAL_PLAN_GENERATION: "meal_plan_generation",
  MOTIVATION_MESSAGE: "motivation_message",
  NOTIFICATION_AI_SYSTEM: "notification_ai_system",
  MEAL_IMAGE_GENERATION: "meal_image_generation",
};

const PROMPT_KEY_LABELS = {
  [PROMPT_KEYS.ASSISTANT_INSTRUCTIONS]:
    "Assistant Instructions",
  [PROMPT_KEYS.MEAL_PLAN_GENERATION]:
    "Meal Plan Generation",
  [PROMPT_KEYS.MOTIVATION_MESSAGE]: "Motivation Message",
  [PROMPT_KEYS.NOTIFICATION_AI_SYSTEM]:
    "Notification AI System",
  [PROMPT_KEYS.MEAL_IMAGE_GENERATION]:
    "Meal Image Generation",
};

const LOCALE_LABELS = {
  az: "Azərbaycan",
  en: "English",
  ru: "Русский",
};

const AI_SERVICES = {
  openai: "OpenAI",
  anthropic: "Anthropic (Claude)",
  google: "Google (Gemini)",
};

const AI_MODELS = {
  openai: [
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini" },
    { value: "gpt-4", label: "GPT-4" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
    {
      value: "dall-e-3",
      label: "DALL-E 3 (Image Generation)",
    },
  ],
  anthropic: [
    {
      value: "claude-3-opus-20240229",
      label: "Claude 3 Opus",
    },
    {
      value: "claude-3-sonnet-20240229",
      label: "Claude 3 Sonnet",
    },
    {
      value: "claude-3-haiku-20240307",
      label: "Claude 3 Haiku",
    },
  ],
  google: [
    { value: "gemini-pro", label: "Gemini Pro" },
    {
      value: "gemini-pro-vision",
      label: "Gemini Pro Vision",
    },
  ],
};

// Variable mappings for each prompt key
const PROMPT_VARIABLES = {
  [PROMPT_KEYS.ASSISTANT_INSTRUCTIONS]: [],
  [PROMPT_KEYS.MOTIVATION_MESSAGE]: [],
  [PROMPT_KEYS.NOTIFICATION_AI_SYSTEM]: [],
  [PROMPT_KEYS.MEAL_IMAGE_GENERATION]: [
    {
      name: "mealName",
      label: "Meal Name",
      description: "Name of the meal",
    },
    {
      name: "ingredientsText",
      label: "Ingredients Text",
      description: "Text describing ingredients",
    },
  ],
  [PROMPT_KEYS.MEAL_PLAN_GENERATION]: [
    {
      name: "daysCount",
      label: "Days Count",
      description: "Number of days for the meal plan",
      category: "Plan Info",
    },
    {
      name: "dayRange",
      label: "Day Range",
      description: 'Range of days (e.g., "day 1-7")',
      category: "Plan Info",
    },
    {
      name: "startDay",
      label: "Start Day",
      description: "Starting day number",
      category: "Plan Info",
    },
    {
      name: "endDay",
      label: "End Day",
      description: "Ending day number",
      category: "Plan Info",
    },
    {
      name: "age",
      label: "Age",
      description: "Customer age",
      category: "Customer Info",
    },
    {
      name: "gender",
      label: "Gender",
      description: "Customer gender",
      category: "Customer Info",
    },
    {
      name: "height",
      label: "Height",
      description: "Customer height in cm",
      category: "Customer Info",
    },
    {
      name: "weight",
      label: "Weight",
      description: "Current weight in kg",
      category: "Customer Info",
    },
    {
      name: "weightTo",
      label: "Target Weight",
      description: "Target weight in kg",
      category: "Customer Info",
    },
    {
      name: "activityLevel",
      label: "Activity Level",
      description: "Activity level number",
      category: "Customer Info",
    },
    {
      name: "goalTypeText",
      label: "Goal Type",
      description: "Weight goal type (lose/gain/maintain)",
      category: "Customer Info",
    },
    {
      name: "calories",
      label: "Calories",
      description: "Daily calorie goal",
      category: "Nutrition",
    },
    {
      name: "proteins",
      label: "Proteins",
      description: "Daily protein goal in grams",
      category: "Nutrition",
    },
    {
      name: "carbs",
      label: "Carbs",
      description: "Daily carbs goal in grams",
      category: "Nutrition",
    },
    {
      name: "fats",
      label: "Fats",
      description: "Daily fats goal in grams",
      category: "Nutrition",
    },
    {
      name: "dietTypeText",
      label: "Diet Type",
      description: "Type of diet",
      category: "Preferences",
    },
    {
      name: "likedIngredients",
      label: "Liked Ingredients",
      description:
        "Comma-separated list of liked ingredients",
      category: "Preferences",
    },
    {
      name: "dislikedIngredients",
      label: "Disliked Ingredients",
      description:
        "Comma-separated list of disliked ingredients",
      category: "Preferences",
    },
    {
      name: "allergiesText",
      label: "Allergies Text",
      description: "Text describing allergies",
      category: "Preferences",
    },
  ],
};

const PromptTemplateForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };
  const isEdit = id !== "new";

  const [formData, setFormData] = useState({
    key: "",
    locale: "az",
    content: "",
    description: "",
    aiService: "openai",
    aiModel: "gpt-4-turbo",
    isActive: true,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const contentTextAreaRef = useRef(null);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const template = await api.getPromptTemplate(id);
      setFormData({
        key: template.key || "",
        locale: template.locale || "az",
        content: template.content || "",
        description: template.description || "",
        aiService: template.aiService || "openai",
        aiModel: template.aiModel || "gpt-4-turbo",
        isActive:
          template.isActive !== undefined
            ? template.isActive
            : true,
      });
    } catch (error) {
      console.error("Error fetching template:", error);
      setError("Failed to load template");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchTemplate();
    }
  }, [isEdit, fetchTemplate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isEdit) {
        await api.updatePromptTemplate(id, formData);
      } else {
        await api.createPromptTemplate(formData);
      }
      navigate("/admin/prompt-templates");
    } catch (error) {
      console.error("Error saving template:", error);
      setError(
        error.response?.data?.message ||
          "Failed to save template"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const insertVariable = (variableName) => {
    const textarea = contentTextAreaRef.current;
    const currentValue = formData.content || "";
    const variableText = `{{${variableName}}}`;

    if (!textarea) {
      // Fallback: append to end
      handleChange("content", currentValue + variableText);
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    const newValue =
      currentValue.substring(0, start) +
      variableText +
      currentValue.substring(end);
    handleChange("content", newValue);

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + variableText.length;
      textarea.setSelectionRange(
        newCursorPos,
        newCursorPos
      );
    }, 0);
  };

  const getAvailableVariables = () => {
    if (!formData.key) return [];
    return PROMPT_VARIABLES[formData.key] || [];
  };

  const getVariablesByCategory = () => {
    const variables = getAvailableVariables();
    if (variables.length === 0) return null;

    const grouped = {};
    variables.forEach((variable) => {
      const category = variable.category || "General";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(variable);
    });

    return grouped;
  };

  if (loading) {
    return (
      <MainLayout>
        <PageContainer theme={theme}>
          <div>Loading...</div>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <Header>
          <Button
            onClick={() =>
              navigate("/admin/prompt-templates")
            }
            startIcon={<ArrowBack />}
            variant="outlined"
          >
            Back
          </Button>
          <h1>
            {isEdit
              ? "Edit Prompt Template"
              : "Create Prompt Template"}
          </h1>
        </Header>

        <FormCard theme={theme}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div
                style={{
                  padding: "12px",
                  marginBottom: "16px",
                  backgroundColor: "#ffebee",
                  color: "#c62828",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            <FormGroup theme={theme}>
              <Label>Prompt Key</Label>
              <Select
                value={formData.key}
                onChange={(e) =>
                  handleChange("key", e.target.value)
                }
                disabled={isEdit}
                required
              >
                <option value="">Select a key</option>
                {Object.entries(PROMPT_KEY_LABELS).map(
                  ([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  )
                )}
              </Select>
              <HelperText>
                The identifier for this prompt template
              </HelperText>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label>Locale</Label>
              <Select
                value={formData.locale}
                onChange={(e) =>
                  handleChange("locale", e.target.value)
                }
                disabled={isEdit}
                required
              >
                {Object.entries(LOCALE_LABELS).map(
                  ([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  )
                )}
              </Select>
              <HelperText>
                Language for this prompt template
              </HelperText>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label>Description</Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
                placeholder="Human-readable description"
              />
              <HelperText>
                Optional description for admin reference
              </HelperText>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label>AI Service</Label>
              <Select
                value={formData.aiService}
                onChange={(e) => {
                  const newService = e.target.value;
                  const availableModels =
                    AI_MODELS[newService] || [];
                  handleChange("aiService", newService);
                  // Reset model to first available model for the new service
                  if (availableModels.length > 0) {
                    handleChange(
                      "aiModel",
                      availableModels[0].value
                    );
                  }
                }}
                required
              >
                {Object.entries(AI_SERVICES).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </Select>
              <HelperText>
                Select the AI service provider for this
                prompt template
              </HelperText>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label>AI Model</Label>
              <Select
                value={formData.aiModel}
                onChange={(e) =>
                  handleChange("aiModel", e.target.value)
                }
                required
              >
                {(AI_MODELS[formData.aiService] || []).map(
                  (model) => (
                    <option
                      key={model.value}
                      value={model.value}
                    >
                      {model.label}
                    </option>
                  )
                )}
              </Select>
              <HelperText>
                Select the AI model to use with the chosen
                service
              </HelperText>
            </FormGroup>

            <FormGroup theme={theme}>
              <Label>Content</Label>
              <TextArea
                ref={contentTextAreaRef}
                value={formData.content}
                onChange={(e) =>
                  handleChange("content", e.target.value)
                }
                placeholder="Enter prompt template content. Use {{variableName}} for variables."
                required
              />
              <HelperText>
                Prompt template content. Use{" "}
                {"{{variableName}}"} syntax for variables
                that will be replaced at runtime.
              </HelperText>

              {formData.key &&
                getAvailableVariables().length > 0 && (
                  <VariablesInfo theme={theme}>
                    <strong>
                      Available Variables (click to insert):
                    </strong>
                    {(() => {
                      const variablesByCategory =
                        getVariablesByCategory();
                      if (!variablesByCategory) return null;

                      return Object.entries(
                        variablesByCategory
                      ).map(([category, vars]) => (
                        <VariableCategory
                          key={category}
                          theme={theme}
                        >
                          {Object.keys(variablesByCategory)
                            .length > 1 && (
                            <VariableCategoryTitle
                              theme={theme}
                            >
                              {category}:
                            </VariableCategoryTitle>
                          )}
                          <VariablesList theme={theme}>
                            {vars.map((variable) => (
                              <VariableTag
                                key={variable.name}
                                theme={theme}
                                onClick={() =>
                                  insertVariable(
                                    variable.name
                                  )
                                }
                                title={
                                  variable.description ||
                                  variable.label
                                }
                              >
                                {`{{${variable.name}}}`}
                              </VariableTag>
                            ))}
                          </VariablesList>
                        </VariableCategory>
                      ));
                    })()}
                  </VariablesInfo>
                )}

              {formData.key &&
                getAvailableVariables().length === 0 && (
                  <VariablesInfo theme={theme}>
                    <div>
                      No variables available for this prompt
                      type.
                    </div>
                  </VariablesInfo>
                )}
            </FormGroup>

            <FormGroup theme={theme}>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleChange(
                      "isActive",
                      e.target.checked
                    )
                  }
                />
                <span>Active</span>
              </CheckboxLabel>
              <HelperText>
                Only active templates will be used by the
                system
              </HelperText>
            </FormGroup>

            <Actions theme={theme}>
              <Button
                type="button"
                variant="outlined"
                onClick={() =>
                  navigate("/admin/prompt-templates")
                }
              >
                Cancel
              </Button>
              <Button
                type="submit"
                startIcon={<Save />}
                loading={saving}
              >
                {isEdit ? "Update" : "Create"} Template
              </Button>
            </Actions>
          </form>
        </FormCard>
      </PageContainer>
    </MainLayout>
  );
};

export default PromptTemplateForm;
